import React, { useState, useEffect } from 'react';
import { Zap, Plus, X, Trash2 } from 'lucide-react';

interface Keyword {
  id: string;
  text: string;
  createdAt: string;
  updatedAt: string;
}

interface KeywordPickerV2Props {
  disabled?: boolean;
  className?: string;
  apiUrl: string;
  user: any;
}

export const KeywordPickerV2: React.FC<KeywordPickerV2Props> = ({
  disabled = false,
  className = "",
  apiUrl,
  user
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeywordText, setNewKeywordText] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Fetch keywords when component mounts
  useEffect(() => {
    if (isOpen && user) {
      fetchKeywords();
    }
  }, [isOpen, user]);

  const fetchKeywords = async () => {
    if (!user || !apiUrl) return;

    setLoading(true);
    try {
      
      // Get JWT token
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const jwtToken = session.tokens?.idToken?.toString();
      
      
      if (!jwtToken) {
        console.error('❌ No JWT token available');
        return;
      }


      const response = await fetch(`${apiUrl}/app/keywords`, {
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
        }
      });


      if (response.ok) {
        const data = await response.json();
        setKeywords(data.keywords || []);
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to fetch keywords:', response.status, errorText);
      }
    } catch (error) {
      console.error('❌ Error fetching keywords:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKeyword = async () => {
    if (!newKeywordText.trim()) return;

    setCreating(true);
    try {
      
      // Get JWT token
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const jwtToken = session.tokens?.idToken?.toString();
      
      
      if (!jwtToken) {
        console.error('❌ No JWT token available');
        return;
      }

      
      const response = await fetch(`${apiUrl}/app/keywords`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({ text: newKeywordText.trim() })
      });


      if (response.ok) {
        await response.json();
        setNewKeywordText('');
        setShowCreateForm(false);
        await fetchKeywords(); // Refresh keywords
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to create keyword:', response.status, errorText);
        throw new Error(`Failed to create keyword: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Error creating keyword:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteKeyword = async (keywordId: string) => {
    setDeleting(keywordId);
    try {
      // Get JWT token
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const jwtToken = session.tokens?.idToken?.toString();
      
      if (!jwtToken) {
        return;
      }

      const response = await fetch(`${apiUrl}/app/keywords`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({ keywordId })
      });

      if (response.ok) {
        await fetchKeywords(); // Refresh keywords
      } else {
        throw new Error('Failed to delete keyword');
      }
    } catch (error) {
      console.error('Error deleting keyword:', error);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-3 ${className}`}
        title="Manage keywords"
      >
        <Zap className="h-4 w-4" />
      </button>
      
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 z-50 w-80 sm:w-80 w-[calc(100vw-2rem)] bg-card border rounded-lg shadow-lg">
          <div className="p-4 border-b">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Keyword Manager</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          
          <div className="p-4 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="text-center py-4 text-muted-foreground">
                Loading keywords...
              </div>
            ) : showCreateForm ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium">Keyword Text</label>
                  <input
                    type="text"
                    value={newKeywordText}
                    onChange={(e) => setNewKeywordText(e.target.value)}
                    placeholder="Enter keyword (word, phrase, or emoji)"
                    className="w-full h-8 rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleCreateKeyword}
                    disabled={creating || !newKeywordText.trim()}
                    className="flex-1 inline-flex items-center justify-center rounded-md text-xs font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-8 px-3"
                  >
                    {creating ? 'Creating...' : 'Create'}
                  </button>
                  <button
                    onClick={() => setShowCreateForm(false)}
                    className="inline-flex items-center justify-center rounded-md text-xs font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-8 px-3"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {keywords.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground text-xs">
                    No keywords yet
                  </div>
                ) : (
                  keywords.map((keyword) => (
                    <div
                      key={keyword.id}
                      className="flex items-center justify-between p-3 rounded-md border border-input bg-background"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-sm">{keyword.text}</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Created: {new Date(keyword.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteKeyword(keyword.id)}
                        disabled={deleting === keyword.id}
                        className="ml-2 p-1 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-destructive hover:text-destructive/80"
                        title="Delete keyword"
                      >
                        {deleting === keyword.id ? (
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  ))
                )}
                
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="w-full inline-flex items-center justify-center rounded-md text-xs font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-8 px-3"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Keyword
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
